/** Grace period after which the page reloads even if no lifecycle event lands. */
const RELOAD_FALLBACK_MS = 3000;

/**
 * Activates a waiting service worker and reloads the page onto the new version.
 *
 * This deliberately does NOT use vite-plugin-pwa's built-in reload. That path
 * reloads inside a `controlling` listener gated on workbox-window's `isUpdate`
 * flag — which is false on the first update after a fresh install (the page is
 * not yet service-worker-controlled). The worker activates but the page never
 * reloads, leaving the update banner spinning forever (vite-plugin-pwa
 * issues #601 / #789).
 *
 * Instead it sends SKIP_WAITING straight to the waiting worker and reloads the
 * moment that worker reaches `activated` — the worker's own lifecycle, which is
 * reliable on iOS Safari (unlike `controllerchange`). A freshly navigated page
 * is always served by the active worker, so no `clients.claim()` is needed. A
 * timeout is the last-resort fallback, and a single-reload guard means whichever
 * path fires first wins.
 */
export function applyUpdate(
  registration: ServiceWorkerRegistration | null,
  reloadPage: () => void = () => window.location.reload(),
): void {
  let reloaded = false;
  const reload = () => {
    if (reloaded) return;
    reloaded = true;
    reloadPage();
  };

  const waiting = registration?.waiting;
  if (!waiting) {
    // No worker is waiting — it may have already activated (e.g. every other
    // tab closed). A plain reload then lands on the new version anyway.
    reload();
    return;
  }

  // Reload once the waiting worker becomes active.
  waiting.addEventListener("statechange", () => {
    if (waiting.state === "activated") reload();
  });
  // It may already be active (another tab, or a prior skip-waiting) — then no
  // further `statechange` will fire, so check synchronously as well.
  if (waiting.state === "activated") reload();

  // Tell the waiting worker to take over now. The generated service worker
  // listens for this message and calls `self.skipWaiting()`.
  waiting.postMessage({ type: "SKIP_WAITING" });

  // Last-resort fallback: if `statechange` somehow never lands, reload anyway —
  // by now the worker has had ample time to activate.
  window.setTimeout(reload, RELOAD_FALLBACK_MS);
}
